import { sendTrackingEvent } from 'actions/workflows/workflows';
import type { AppDispatch } from 'store';

interface ZopimSessionCheckParams {
  conversationId: string | undefined;
  dispatch: AppDispatch;
  isPreview: boolean;
  liveChatStatus: string | undefined;
}

export const checkAndTrackZopimSessionMismatch = async ({
  conversationId,
  dispatch,
  isPreview,
  liveChatStatus,
}: ZopimSessionCheckParams): Promise<void> => {
  try {
    // Skip if required values are not available
    if (!conversationId || !liveChatStatus) {
      return;
    }

    // Get real-time zChat.isChatting() via postMessage
    const isChatting = await getZChatIsChatting();

    /*
      liveChatStatus === 'active' means a Zopim chat session has been initiated.
      isChatting === false indicates that the chat has ended on the Zopim side.
      This occurs when the user closes the FT widget page, then later returns 
      and resumes the conversation.
    */
    if (liveChatStatus === 'active' && isChatting === false) {
      dispatch(
        sendTrackingEvent({
          conversation_id: conversationId,
          description:
            'Zopim chat session mismatch detected - liveChatStatus shows active but Zopim isChatting is false',
          event_type: 'zopim-agent-session-mismatch',
          is_preview: isPreview,
        }),
      );
    }
  } catch (error) {}
};

/**
 * Get real-time zChat.isChatting() status from Zopim widget API
 * via postMessage communication across iframe boundary
 */
const getZChatIsChatting = (): Promise<boolean | undefined> => {
  return new Promise(resolve => {
    const messageId = `zChat-isChatting-${Date.now()}-${Math.random()}`;

    const handleMessage = (event: MessageEvent) => {
      if (
        event.data.event === 'forethoughtZChatIsChatting' &&
        event.data.messageId === messageId
      ) {
        window.removeEventListener('message', handleMessage);
        clearTimeout(timeoutId);
        resolve(event.data.isChatting);
      }
    };

    window.addEventListener('message', handleMessage);

    // Send message to parent page to check zChat.isChatting()
    window.parent.postMessage(
      {
        event: 'forethoughtCheckZChatIsChatting',
        messageId: messageId,
      },
      '*',
    );

    // Timeout after 500ms if no response
    const timeoutId = setTimeout(() => {
      window.removeEventListener('message', handleMessage);
      resolve(undefined);
    }, 500);
  });
};
